// /////////////////////////////////////////////////////////////
// [ファイル] useObcSyncJob.ts
// [概要] OBC 月次同期の非同期ジョブ (Cloud Tasks) を起動し、進捗・履歴を
//        Firestore でリアルタイム監視するフック。
//        旧来の trn_obc_sync_log 60秒ポーリングを置き換える。
//        - start(): start_obc_sync を叩いてバックエンドへ同期を委譲 (UI はブロックしない)
//        - jobs:   最近のジョブ履歴 (onSnapshot)
//        - activeJob: 進行中ジョブ (queued/running) のライブ進捗
// /////////////////////////////////////////////////////////////
import { useEffect, useState, useCallback } from 'react';
import {
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore';
import axios from 'axios';
import { db } from '../firebase/firebase';
import { getIdToken } from '../firebase/getIdToken';
import { ep } from '../api/endpoints';

const JOBS = 'HozenallCloud_Obc_Sync_Jobs';
const START_API_URL = ep('start_obc_sync');

export type ObcSyncJobStatus = 'queued' | 'running' | 'completed' | 'failed';

export interface ObcSyncJobError {
  task: string;
  message: string;
}

export interface ObcSyncJob {
  id: string;
  status: ObcSyncJobStatus;
  year: number;
  month: number;
  total: number;
  done: number;
  currentTask: string;
  recordsSynced: number;
  errors: ObcSyncJobError[];
  startedBy: string;
  startedAt: number; // ミリ秒。未確定時は 0
  completedAt: number; // ミリ秒。未完了時は 0
}

function toMillis(value: unknown): number {
  return value instanceof Timestamp ? value.toMillis() : 0;
}

export const useObcSyncJob = () => {
  const [jobs, setJobs] = useState<ObcSyncJob[]>([]);
  const [loading, setLoading] = useState(true);

  // 最近のジョブを購読 (履歴一覧＋進行中ジョブの両方をここから供給)
  useEffect(() => {
    const q = query(collection(db, JOBS), orderBy('started_at', 'desc'), limit(20));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows: ObcSyncJob[] = snap.docs.map((d) => {
          const data = d.data() as Record<string, unknown>;
          return {
            id: d.id,
            status: (data.status as ObcSyncJobStatus) || 'queued',
            year: (data.year as number) || 0,
            month: (data.month as number) || 0,
            total: (data.total as number) || 0,
            done: (data.done as number) || 0,
            currentTask: (data.current_task as string) || '',
            recordsSynced: (data.records_synced as number) || 0,
            errors: (data.errors as ObcSyncJobError[]) || [],
            startedBy: (data.started_by as string) || '',
            startedAt: toMillis(data.started_at),
            completedAt: toMillis(data.completed_at),
          };
        });
        setJobs(rows);
        setLoading(false);
      },
      (err) => {
        console.error('OBC同期ジョブの購読に失敗:', err);
        setLoading(false);
      },
    );
    return () => unsub();
  }, []);

  // 進行中ジョブ (queued/running) の最新 1 件 = ライブ進捗の対象
  const activeJob = jobs.find((j) => j.status === 'queued' || j.status === 'running') || null;

  // 同期を起動 (バックエンドへ委譲。完了は待たず、進捗は Firestore 購読で受ける)
  const start = useCallback(async (year: number, month: number) => {
    const token = await getIdToken();
    if (!token) throw new Error('認証情報を取得できませんでした');
    const res = await axios.post(
      START_API_URL,
      { year, month },
      { headers: { Authorization: `Bearer ${token}` } },
    );
    if (res.data?.status !== 'success') {
      throw new Error(res.data?.message || '同期の起動に失敗しました');
    }
    return res.data.job_id as string;
  }, []);

  return { jobs, activeJob, loading, start };
};
